// Kódování UI layoutu do/z URL fragmentu (`#…`). Fragment se NEposílá na server, takže
// query (logické agendy + stackId) zůstává malá a layout přežije reload + je sdílitelný.
//
// Obsah snapshotu:
//  - `w`: POZIČNÍ pole dle pořadí navigačních vrstev — jen ne-defaultní okna { i, m?, r? }.
//         Pořadí vrstev round-tripuje přes query (serializer), takže index i je stabilní.
//  - `o`: stackOrigin mapa { stackId: originStackId } — stackId round-tripuje přes query `sid`.
// Kóduje se base64url (payload je ASCII: čísla, módy, stackId `s_<uuid>`) → bezpečné v hashi
// i při průchodu TanStack routerem (žádné %-escapování).

import type { WindowMode, WindowRect, WindowUiState } from './windowManagerTypes';

interface LayoutEntry {
  i: number;
  m?: WindowMode;
  r?: [number, number, number, number];
}

export interface LayoutSnapshot {
  v: 1;
  w: LayoutEntry[];
  o: Record<string, string>;
}

function toBase64Url(json: string): string {
  return btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): string {
  return atob(s.replace(/-/g, '+').replace(/_/g, '/'));
}

const round = (n: number): number => Math.round(n);

/** Serializuje layout do base64url řetězce (pro `#…`). Prázdné → `''` (kotva se smaže). */
export function encodeLayout(
  layerIds: readonly string[],
  windows: Readonly<Record<string, WindowUiState>>,
  stackOrigin: Readonly<Record<string, string>>
): string {
  const w: LayoutEntry[] = [];
  layerIds.forEach((id, i) => {
    const ui = windows[id];
    if (!ui) return;
    const entry: LayoutEntry = { i };
    let meaningful = false;
    if (ui.mode && ui.mode !== 'docked') {
      entry.m = ui.mode;
      meaningful = true;
    }
    if (ui.rect) {
      entry.r = [round(ui.rect.x), round(ui.rect.y), round(ui.rect.w), round(ui.rect.h)];
      meaningful = true;
    }
    if (meaningful) w.push(entry);
  });

  const o = { ...stackOrigin };
  if (w.length === 0 && Object.keys(o).length === 0) return '';

  const snapshot: LayoutSnapshot = { v: 1, w, o };
  return toBase64Url(JSON.stringify(snapshot));
}

/** Deserializuje layout z base64url řetězce. Nevalidní/prázdné → null. */
export function decodeLayout(raw: string): LayoutSnapshot | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(fromBase64Url(raw)) as LayoutSnapshot;
    if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.w)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function rectFromEntry(r: [number, number, number, number]): WindowRect {
  return { x: r[0], y: r[1], w: r[2], h: r[3] };
}
